import { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useRootSelector } from '@/hooks/selector.hook';
import { CustomerNotificationReadStatus } from '@/modules/notifications/states/notification-read-status.state';
import { getNotificationIcon, getNotificationIconClassName } from '@/modules/notifications/components/notification-icon';
import { useNotificationService } from '@/services/notification.service';
import { Notification } from '@/types/notification';
import { cn } from '@/utils/classnames';
import { formatNotificationTime } from '@/utils/formatter';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SeenIcon } from './icon';

const DROPDOWN_PAGE_SIZE = 3;

const NotificationList = ({ items }: { items: Notification[] }) => {
  if (items.length === 0) {
    return (
      <div className="flex min-h-[18rem] items-center justify-center rounded-[0.8rem] border border-primary bg-[#FFFFFF] p-[1.6rem] dark:bg-action-secondary">
        <p className="text-[1.4rem] font-medium leading-[2rem] sub-text">Không có thông báo</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[0.8rem]">
      {items.map((item) => {
        const Icon = getNotificationIcon(item.type);

        return (
          <article
            key={item.id}
            className={cn(
              'flex gap-[1.2rem] rounded-[0.8rem] border px-[1.2rem] py-[1.2rem] shadow-[0px_2px_6px_0px_rgba(0,28,89,0.04)]',
              item.isRead
                ? 'border-primary bg-[#FFFFFF] dark:bg-action-secondary'
                : 'border-[#CADBFF] bg-[#EBF3FF] dark:border-[#1D375E] dark:bg-[#1D375E]',
            )}
          >
            <div
              className={cn(
                'flex h-[4.2rem] w-[4.2rem] shrink-0 items-center justify-center rounded-full border [&_path]:fill-current [&_path]:stroke-current [&_svg]:h-[1.8rem] [&_svg]:w-[1.8rem]',
                getNotificationIconClassName(item.type),
              )}
            >
              <Icon />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-start justify-between gap-[1rem]">
                <h3 className="min-w-0 flex-1 truncate text-[1.4rem] font-semibold leading-[2rem] text-main">
                  {item.title}
                </h3>

                <div className="flex shrink-0 items-center gap-[0.6rem] pt-[0.1rem]">
                  <span className="whitespace-nowrap text-[1.4rem] font-medium leading-[1.8rem] sub-text">
                    {formatNotificationTime(item.createdAtUtc, true)}
                  </span>
                  {!item.isRead ? (
                    <span className="h-[0.7rem] w-[0.7rem] rounded-full bg-[#2563EB]" />
                  ) : (
                    <span className="text-[1.4rem] font-semibold leading-none text-[#7988A4]"><SeenIcon className='w-auto h-[1.3rem]' /></span>
                  )}
                </div>
              </div>

              <p
                className=" text-[1.4rem] font-normal leading-[2rem] sub-text"
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                }}
              >
                {item.body || '--'}
              </p>
            </div>
          </article>
        );
      })}
    </div>
  );
};

interface NotificationDropdownProps {
  isGuest?: boolean;
  compact?: boolean;
}

export default function NotificationDropdown({ isGuest = false, compact = false }: NotificationDropdownProps) {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isGuestDialogOpen, setIsGuestDialogOpen] = useState(false);
  const [activeReadStatus, setActiveReadStatus] = useState<CustomerNotificationReadStatus | undefined>();
  const {
    getNotificationDropdown,
    getNotificationStatistics,
    markAllAsRead,
  } = useNotificationService();
  const { isAuthenticated } = useRootSelector((state) => state.auth);
  const notifications = useRootSelector((state) => state.notificationDropdown.items);
  const notificationPagination = useRootSelector((state) => state.notificationDropdown.pagination);
  const statisticUnreadCount = useRootSelector((state) => state.notification.statisticUnreadCount);
  const navigate = useNavigate();
  const activeTabValue = activeReadStatus ? String(activeReadStatus) : 'all';
  const canShowGuestUI = useMemo(() => isGuest && !isAuthenticated, [isGuest, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    getNotificationStatistics();
  }, [getNotificationStatistics, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !isNotificationOpen) return;

    getNotificationDropdown({
      readStatus: activeReadStatus,
      pageNumber: 1,
      pageSize: DROPDOWN_PAGE_SIZE,
    });
    getNotificationStatistics();
  }, [
    activeReadStatus,
    getNotificationDropdown,
    getNotificationStatistics,
    isAuthenticated,
    isNotificationOpen,
  ]);

  const handleTabChange = (value: string) => {
    setActiveReadStatus(
      value === 'all' ? undefined : Number(value) as CustomerNotificationReadStatus,
    );
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    getNotificationDropdown({
      readStatus: activeReadStatus,
      pageNumber: 1,
      pageSize: DROPDOWN_PAGE_SIZE,
    });
    getNotificationStatistics();
  };

  const handleViewAll = () => {
    setIsNotificationOpen(false);
    navigate('/admin/notifications');
  };

  if (!isAuthenticated && !canShowGuestUI) return null;

  return (
    <>
      <DropdownMenu open={isNotificationOpen} onOpenChange={setIsNotificationOpen}>
        <DropdownMenuTrigger asChild>
          <div className="relative">
            <button
              type="button"
              className={cn(
                'flex shrink-0 items-center justify-center rounded-full transition-colors duration-200',
                compact ? '!h-[3.2rem] !w-[3.2rem]' : '!h-[4rem] !w-[4rem]',
                isNotificationOpen
                  ? 'bg-[#3463FD] text-[#FFFFFF] lg:bg-[#E8F3FF] lg:text-[#1C37E0] dark:lg:bg-[#212B3B] dark:lg:text-[#969AA1]'
                  : 'bg-[#3463FD] text-[#FFFFFF] lg:bg-[#F4F7FE] lg:text-[#969AA1] dark:lg:bg-[#212B3B] dark:lg:text-[#969AA1]',
              )}
              onClick={(event) => {
                if (!canShowGuestUI) return;
                event.preventDefault();
                event.stopPropagation();
                setIsNotificationOpen(false);
                setIsGuestDialogOpen(true);
              }}
            >
              <svg
                width={compact ? '13' : '15'}
                height={compact ? '16' : '18'}
                viewBox="0 0 20 23"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.943 16.0335C18.3951 15.0383 17.5805 12.2226 17.5805 8.54492C17.5805 2.13438 10.9983 -1.87221 5.73245 1.33306C3.28858 2.82063 1.78309 5.56977 1.78309 8.54492C1.78309 12.2236 0.967554 15.0383 0.419576 16.0335C-0.193026 17.141 0.561316 18.5324 1.77739 18.538L1.7831 18.538H5.81242C6.42115 21.6783 10.0261 22.9463 12.3013 20.8203C12.9413 20.2223 13.38 19.4214 13.5512 18.538H17.5805C18.7966 18.5362 19.5549 17.1472 18.9454 16.0378L18.943 16.0335ZM9.6818 20.2035C8.67787 20.2031 7.78309 19.5359 7.44844 18.538H11.9152C11.5805 19.5359 10.6857 20.2031 9.6818 20.2035ZM1.78309 16.8725C2.54334 15.4942 3.36283 12.3006 3.36283 8.54492C3.36283 3.41649 8.62864 0.211207 12.8413 2.77543C14.7964 3.96549 16.0008 6.1648 16.0008 8.54492C16.0008 12.2975 16.8183 15.4911 17.5805 16.8725H1.78309Z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="0.40625"
                />
              </svg>
            </button>

            {isAuthenticated && statisticUnreadCount > 0 && (
              <div className="absolute right-[-0.4rem] top-[-0.4rem] flex h-[1.8rem] min-w-[1.8rem] items-center justify-center rounded-full border-[2px] border-[#FFFFFF] bg-[#DC3545] px-[0.4rem] text-[1.4rem] font-semibold leading-none text-[#FFFFFF]">
                {statisticUnreadCount > 99 ? '99+' : statisticUnreadCount}
              </div>
            )}
          </div>
        </DropdownMenuTrigger>

        {isAuthenticated ? (
          <DropdownMenuContent
            className="w-[calc(100vw-2.4rem)] max-w-[42rem] rounded-[1.2rem] border border-primary bg-[#FFFFFF] p-[1.4rem] shadow-[0px_8px_24px_0px_rgba(0,28,89,0.14)] dark:border-disable dark:bg-main sm:w-[40rem]"
            align="end"
            collisionPadding={16}
          >
            <div className="flex items-center justify-between gap-[1.2rem]">
              <div className="flex min-w-0 items-center gap-[0.8rem]">
                <h3 className="text-[1.6rem] font-semibold leading-[2.2rem] text-main">
                  Thông báo
                </h3>
                <span className="rounded-[0.4rem] bg-[#E9EFFD] px-[0.7rem] py-[0.25rem] text-[1.4rem] font-semibold leading-[1.8rem] text-[#2563EB] dark:bg-[#225AD629] dark:text-[#9BB7F6]">
                  {statisticUnreadCount} chưa đọc
                </span>
              </div>

              <button
                type="button"
                className="shrink-0 text-[1.4rem] font-semibold leading-[2rem] text-[#2563EB] outline-none transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 dark:text-[#9BB7F6]"
                disabled={statisticUnreadCount === 0}
                onClick={handleMarkAllAsRead}
              >
                Đánh dấu tất cả đã đọc
              </button>
            </div>

            <p className="mt-[1.2rem] text-[1.4rem] font-normal leading-[2rem] sub-text">
              Cập nhật mới nhất từ đơn hàng, hồ sơ và khách hàng
            </p>

            <Tabs value={activeTabValue} onValueChange={handleTabChange} className="mt-[1.4rem]">
              <TabsList className="grid h-auto w-full grid-cols-3 gap-[1rem] bg-transparent p-0">
                <TabsTrigger
                  value="all"
                  className="h-[3.2rem] rounded-[0.8rem] border border-primary bg-[#FFFFFF] px-[1rem] text-[1.4rem] font-semibold leading-[2rem] text-main shadow-none data-[state=active]:border-[#E9EFFD] data-[state=active]:bg-[#E9EFFD] data-[state=active]:text-[#2563EB] dark:bg-action-secondary dark:data-[state=active]:bg-[#225AD629]"
                >
                  Tất cả
                </TabsTrigger>
                <TabsTrigger
                  value={String(CustomerNotificationReadStatus.Unread)}
                  className="h-[3.2rem] rounded-[0.8rem] border border-primary bg-[#FFFFFF] px-[1rem] text-[1.4rem] font-semibold leading-[2rem] text-main shadow-none data-[state=active]:border-[#E9EFFD] data-[state=active]:bg-[#E9EFFD] data-[state=active]:text-[#2563EB] dark:bg-action-secondary dark:data-[state=active]:bg-[#225AD629]"
                >
                  Chưa đọc
                </TabsTrigger>
                <TabsTrigger
                  value={String(CustomerNotificationReadStatus.Read)}
                  className="h-[3.2rem] rounded-[0.8rem] border border-primary bg-[#FFFFFF] px-[1rem] text-[1.4rem] font-semibold leading-[2rem] text-main shadow-none data-[state=active]:border-[#E9EFFD] data-[state=active]:bg-[#E9EFFD] data-[state=active]:text-[#2563EB] dark:bg-action-secondary dark:data-[state=active]:bg-[#225AD629]"
                >
                  Đã đọc
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="mt-[1.2rem]">
                <NotificationList items={notifications} />
              </TabsContent>
              <TabsContent value={String(CustomerNotificationReadStatus.Unread)} className="mt-[1.2rem]">
                <NotificationList items={notifications} />
              </TabsContent>
              <TabsContent value={String(CustomerNotificationReadStatus.Read)} className="mt-[1.2rem]">
                <NotificationList items={notifications} />
              </TabsContent>
            </Tabs>

            <div className="mt-[1.4rem] flex items-center justify-between gap-[1rem]">
              <p className="text-[1.4rem] font-normal leading-[2rem] text-main">
                Hiển thị {notifications.length} trên {notificationPagination.totalElements} thông báo
              </p>

              <button
                type="button"
                onClick={handleViewAll}
                className="flex shrink-0 items-center gap-[0.6rem] text-[1.4rem] font-semibold leading-[2rem] text-[#2563EB] transition-opacity hover:opacity-80 dark:text-[#9BB7F6]"
              >
                Xem tất cả
                <ChevronRight className="h-[1.6rem] w-[1.6rem]" />
              </button>
            </div>
          </DropdownMenuContent>
        ) : null}
      </DropdownMenu>

      {canShowGuestUI ? (
        <Dialog open={isGuestDialogOpen} onOpenChange={setIsGuestDialogOpen}>
          <DialogContent className="w-[calc(100%-3.2rem)] max-w-[42rem] rounded-[1.2rem] border border-[#E2E8F0] bg-[#FFFFFF] p-[2rem] dark:border-[#22304A] dark:bg-[#091327]">
            <DialogHeader className="space-y-[0.8rem]">
              <DialogTitle className="text-[1.8rem] font-semibold leading-[2.4rem] text-[#212B36] dark:text-[#E9EFFD]">
                Thông báo
              </DialogTitle>
              <DialogDescription className="text-[1.4rem] leading-[2rem] text-[#637381] dark:text-[#9FB0D0]">
                Đăng nhập để xem thông báo
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="mt-[2rem] flex-row justify-end gap-[1rem]">
              <Button
                type="button"
                variant="outline"
                size="size40"
                onClick={() => setIsGuestDialogOpen(false)}
                className="border-[#D0D7E2] text-[#212B36] dark:border-[#31415F] dark:text-[#E9EFFD]"
              >
                Hủy
              </Button>

              <Button
                type="button"
                variant="default"
                size="size40"
                onClick={() => {
                  setIsGuestDialogOpen(false);
                  navigate('/login');
                }}
              >
                Đăng nhập
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
